import { useCallback, useMemo, useState } from 'react';
import { ServiceDeployConfig, SITE_BATCH_SERVICE_CATALOG } from '../../../models/SiteBatch';

export type DeployMode = 'build' | 'reuse' | 'skip';

/**
 * Per-service selection state for the deploy dialog.
 *
 * - `build` (default): deploy with a fresh image build
 * - `reuse`: deploy by re-tagging an existing versioned image (requires
 *   target_version; drops the field when switching back to build)
 * - `skip`: do NOT deploy this service in the current batch. Skipped
 *   appnames are excluded from `toPayload()`, so the backend's
 *   `FilterSteps` drops them and no deployment_results row is created.
 *   (deploy-service-skip feature)
 */
export interface ServiceSelection {
  mode: DeployMode;
  target_version?: string;
}

export type SelectionMap = Map<string /* appname */, ServiceSelection>;

const DEFAULT_SELECTION: ServiceSelection = { mode: 'build' };

/**
 * Appnames for which reuse mode is disallowed by the workflow itself
 * (see .github/workflows/deploy.yaml guards). The UI should disable the
 * reuse radio on these rows. `config-only` isn't part of the 8-service
 * catalog but lives here for defensive symmetry with the backend guard.
 */
export const REUSE_INCOMPATIBLE_APPNAMES: ReadonlySet<string> = new Set([
  'config-only',
]);

/**
 * Sites for which reuse mode is disallowed. Infra sites have no
 * per-service versioned images, so the whole row set is locked into
 * build mode. Mirrors `infraSites` in batchOrchestrator.go.
 */
export const REUSE_INCOMPATIBLE_SITES: ReadonlySet<string> = new Set([
  'infra',
]);

/**
 * Whether reuse mode is allowed for a given (site, appname) combo.
 * Used by ServiceModeRow to toggle the radio's disabled state and the
 * tooltip explaining *why* reuse is blocked.
 */
export function isReuseAllowed(site: string, appname: string): boolean {
  if (REUSE_INCOMPATIBLE_SITES.has(site)) return false;
  if (REUSE_INCOMPATIBLE_APPNAMES.has(appname)) return false;
  return true;
}

export interface UseDeployServiceSelectionResult {
  selection: SelectionMap;
  /** Overwrite the full map — typically used only by tests. */
  setSelection: (next: SelectionMap) => void;
  /** Update one service's mode/version; coerces invalid combos. */
  updateService: (appname: string, next: ServiceSelection) => void;
  /** Reset every service back to build mode (no version). */
  reset: () => void;
  /**
   * 모든 row 를 동일한 mode 로 일괄 변경. reuse 일괄 적용 시 target_version 은
   * 비워두고 운영자가 row 별로 채워야 한다 (isReady=false 상태 유지).
   *
   * `site` 인자가 주어지면 isReuseAllowed(site, appname) 가 false 인 행은
   * 'reuse' 일괄에서 제외하고 기존 mode 를 보존한다. 사이트/서비스가
   * reuse-incompatible 인 경우에도 일괄 버튼 누른 결과가 무효 payload 가
   * 되지 않도록 하는 방어.
   */
  setAllMode: (mode: DeployMode, site?: string) => void;
  /**
   * True when the selection is ready to submit: every reuse-mode row
   * has a target_version picked. Build-mode rows are always ready.
   */
  isReady: boolean;
  /**
   * Payload for StartBatchRequest.services[]. Only non-default selections
   * are included to keep the request body lean; if every service stays
   * build-mode, toPayload() returns an empty array (legacy flow).
   */
  toPayload: () => ServiceDeployConfig[];
}

function buildDefaultSelection(appnames: readonly string[]): SelectionMap {
  return new Map(appnames.map((a) => [a, { ...DEFAULT_SELECTION }]));
}

/**
 * `useDeployServiceSelection` — state store for the deploy dialog's
 * per-service mode/version table.
 *
 * Defaults every service to build mode (backward-compatible with the
 * pre-feature deploy button that just posted `{site: ...}` with no
 * services[]). Serializes the current selection into the backend's
 * ServiceDeployConfig[] shape via `toPayload()`.
 *
 * @param appnames Optional override for the list of appnames to manage.
 *   Defaults to the canonical 8-service catalog — tests use this hook
 *   directly on a smaller set to avoid coupling to batch ordering.
 */
export function useDeployServiceSelection(
  appnames: readonly string[] = SITE_BATCH_SERVICE_CATALOG.map((s) => s.appname),
): UseDeployServiceSelectionResult {
  const [selection, setSelection] = useState<SelectionMap>(() =>
    buildDefaultSelection(appnames),
  );

  const updateService = useCallback(
    (appname: string, next: ServiceSelection) => {
      setSelection((prev) => {
        const copy = new Map(prev);
        if (next.mode === 'build' || next.mode === 'skip') {
          // Drop target_version when the row leaves reuse mode so stale
          // selections don't leak into the payload or linger if the
          // operator flips reuse → skip → reuse.
          copy.set(appname, { mode: next.mode });
        } else {
          copy.set(appname, { mode: 'reuse', target_version: next.target_version });
        }
        return copy;
      });
    },
    [],
  );

  const reset = useCallback(() => {
    setSelection(buildDefaultSelection(appnames));
  }, [appnames]);

  const setAllMode = useCallback(
    (mode: DeployMode, site?: string) => {
      setSelection((prev) => {
        const next = new Map<string, ServiceSelection>();
        for (const appname of appnames) {
          // reuse 일괄: site/appname 이 reuse-incompatible 이면 기존 선택 유지.
          // build/skip 일괄: 무조건 모두 적용 (호환성 제약 없음).
          if (mode === 'reuse' && site != null && !isReuseAllowed(site, appname)) {
            next.set(appname, prev.get(appname) ?? { ...DEFAULT_SELECTION });
            continue;
          }
          if (mode === 'reuse') {
            // target_version 은 운영자가 row 별로 채우도록 비워둔다.
            next.set(appname, { mode: 'reuse' });
          } else {
            next.set(appname, { mode });
          }
        }
        return next;
      });
    },
    [appnames],
  );

  const isReady = useMemo(() => {
    let hasDeployable = false;
    for (const value of selection.values()) {
      if (value.mode === 'reuse' && !value.target_version) {
        return false;
      }
      if (value.mode !== 'skip') {
        hasDeployable = true;
      }
    }
    // deploy-service-skip: 전체 skip 시 비활성화 — 배포할 서비스가 없으면
    // backend도 거부하지만 운영자에게 FE에서 미리 차단하는 편이 친절.
    return hasDeployable;
  }, [selection]);

  const toPayload = useCallback((): ServiceDeployConfig[] => {
    // Backend contract (module-5): `services=[]` means "all 8 canonical
    // services deploy in build mode" (backward compat). Any deviation
    // from that default (reuse or skip) requires a non-empty payload
    // listing every appname we DO want deployed — `FilterSteps` uses
    // this list as the exact set to dispatch, and anything missing is
    // dropped (so no deployment_results row for skipped services).
    const hasNonDefault = Array.from(selection.values()).some(
      (s) => s.mode !== 'build',
    );
    if (!hasNonDefault) {
      return [];
    }
    return appnames
      .filter((appname) => selection.get(appname)?.mode !== 'skip')
      .map<ServiceDeployConfig>((appname) => {
        const sel = selection.get(appname) ?? DEFAULT_SELECTION;
        if (sel.mode === 'reuse' && sel.target_version) {
          return {
            appname,
            deploy_mode: 'reuse',
            target_version: sel.target_version,
          };
        }
        return { appname, deploy_mode: 'build' };
      });
  }, [appnames, selection]);

  return { selection, setSelection, updateService, reset, setAllMode, isReady, toPayload };
}
